import { resizeRect } from 'rise-ui/utils/dom.js';
import { maxOne, sumOne } from 'rise-ui/utils/array.js';
import { getBoundsOfRectangle } from 'rise-ui/utils/math.js';

const matchNewline = /\r?\n/;
const matchWhitespace = /\s+/;

export function drawLineSegment(context, x, y, pathStart = false) {
  if (pathStart) {
    context.moveTo(x, y);
  } else {
    context.lineTo(x, y);
  }
}

export function drawLineSegments(context, segments, options = null) {
  if (!segments?.length) {
    return;
  }
  const { x: X = 'x', y: Y = 'y', transform } = options || {};
  let x, y;
  for (let i = 0; i < segments.length; ++i) {
    const segment = transform ? transform(segments[i]) : segments[i];
    if (Array.isArray(segment)) {
      [ x, y ] = segment;
    } else {
      ({ [X]: x, [Y]: y } = segment);
    }
    if (i) {
      context.lineTo(x, y);
    } else {
      context.moveTo(x, y);
    }
  }
}

export function drawLines(context, lines, options) {
  for (let i = 0; i < lines.length; ++i) {
    drawLineSegments(context, lines[i], options);
  }
}

export function setDprTransform(context, ratio = null) {
  ratio ||= window.devicePixelRatio;
  ratio !== 1 && context?.setTransform(ratio, 0, 0, ratio, 0, 0);
}

export function getContext(canvas, contextType = '2d', contextOptions = {}, ratio = null) {
  const context = canvas?.getContext(contextType || '2d', contextOptions);
  setDprTransform(context, ratio);
  return context;
}

export function clearCanvas(canvasOrContext, backgroundColor, width, height, contextOptions = {}) {
  const isCanvas = canvasOrContext instanceof HTMLCanvasElement;
  const canvas = isCanvas ? canvasOrContext : canvasOrContext.canvas;
  const resized = resizeRect(canvas, width, height, window.devicePixelRatio);
  if (resized && !backgroundColor) {
    return resized; // Already cleared by nature of canvas resize
  }
  const context = isCanvas ? getContext(canvas, '2d', contextOptions)
    : canvasOrContext;
  if (!backgroundColor || backgroundColor.startsWith('rgba(')) {
    context.clearRect(0, 0, width, height);
  }
  if (backgroundColor) {
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, width, height);
  }
  return resized;
}

export function createCanvas(width = null, height = null, backgroundColor = null, contextOptions = {}, ratio = null) {
  ratio ||= window.devicePixelRatio;
  const canvas = document.createElement('canvas');
  if (width !== null && width !== undefined) {
    canvas.style.width = `${Math.floor(width)}px`;
    canvas.width = width = Math.floor(width * ratio);
  }
  if (height !== null && height !== undefined) {
    canvas.style.height = `${Math.floor(height)}px`;
    canvas.height = height = Math.floor(height * ratio);
  }
  if (backgroundColor) {
    const context = getContext(canvas, '2d', contextOptions, ratio);
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, width, height);
  }
  return canvas;
}

export function hintCanvas(hint, canvas) {
  canvas.dataset.canvasHint = hint;
  return canvas;
}

/**
 * Align a coordinate for crisp orthogonal lines on canvas
 * @param {number} v
 * @returns {number}
 */
export function alignForLine(v) {
  const r = Math.round(v);
  return r + (r > v ? -.5 : .5);
}

/**
 * Align a coordinate for crisp text on canvas
 * @param {number} v
 * @returns {number}
 */
export function alignForText(v) {
  return Math.round(v);
}

/**
 * @name GetCanvasTextSizeOptions
 * @param {HTMLCanvasElement} [canvas] Either canvas or context is required
 * @param {CanvasRenderingContext2D} [context] Either canvas or context is required
 * @property {number} [fontSize] Font size
 * @property {string} [fontFamily] (default: sans-serif)
 * @property {string} [font] Alias to fontFamily
 * @property {string} [fontFace] Alias to fontFamily
 * @property {string} [fontMultiplier] Multipler applied to given fontSize
 * @property {number} [rotate] Degrees to rotate text before measurement
 */
/**
 * Gives expected size of a single line of text on canvas
 * @param {string} text
 * @param {GetCanvasTextSizeOptions} options
 * @returns {{width: number, height: number}}
 */
export function getCanvasTextSize(text, options) {
  let ctx = options?.context
    || getContext(options?.canvas, '2d', options?.contextOptions, options?.ratio);
  if (!ctx) {
    throw new Error('Expected canvas/context');
  }
  const fontFamily = options.fontFamily || options.fontFace || options.font || 'sans-serif';
  let fontSize = options.fontSize;
  if (fontSize) {
    fontSize = fontSize * (options.fontMultiplier || 1);
  }
  if (fontFamily) {
    ctx.font = (fontSize ? `${fontSize}px ` : '') + fontFamily;
  }
  let size = {
    width:  ctx.measureText(text).width,
    height: fontSize
  };
  if (options.rotate) {
    size = getBoundsOfRectangle(size, options.rotate);
  }
  return size;
}

/**
 * @name GetCanvasTextSizeText
 * @type {string|GetCanvasTextSizeRect}
 */
/**
 * @name GetCanvasTextSizeRect
 * @property {number} width
 * @property {number} height
 */
/**
 * @name GetCanvasWrappedSizeOptions
 * @property {HTMLCanvasElement} [canvas] Either canvas or context is required
 * @property {CanvasRenderingContext2D} [context] Either canvas or context is required
 * @property {number} [width] Width to wrap at
 * @property {number} [fontSize] Font size
 * @property {string} [fontFamily] (default: sans-serif)
 * @property {string} [font] Alias to fontFamily
 * @property {string} [fontFace] Alias to fontFamily
 * @property {number} [lineGap] Gap to insert between measured lines
 */
/**
 * @name GetCanvasWrappedSizeResult
 * @property {number} width Given or expected width of measured text/rects
 * @property {number} height Expected height of measured text/rects
 * @property {GetCanvasWrappedSizeResultLine[]} lines Measured lines of text/rects
 */
/**
 * @name GetCanvasWrappedSizeResultLine
 * @property {number} width Expected width of measured line of text/rects
 * @property {number} height Expected height of measured line of text/rects
 * @property {GetCanvasWrappedSizeResultText[]} lines Measured lines of text/rects
 */
/**
 * @name GetCanvasWrappedSizeResultText
 * @property {number} width Given or expected width of measured text/rect
 * @property {number} height Given or expected height of measured text/rect
 * @property {string} [text] Given text
 * @property {GetCanvasTextSizeRect} [rect] Given rect
 */
/**
 * Gives expected size of wrapped text/rects on canvas
 * @param {GetCanvasTextSizeText|GetCanvasTextSizeText[]} text
 * @param {GetCanvasWrappedSizeOptions} options
 * @returns {GetCanvasWrappedSizeResult}
 */
export function getCanvasWrappedSize(text, options) {
  const spaceSize = getCanvasTextSize(' ', options);
  const width = options.width || Infinity;
  const gap = options.gap || 0;
  const limit = options.limit || Infinity;
  const result = { width: 0, height: 0, lines: [] };

  if (!Array.isArray(text)) {
    text = [ text ];
  }
  let i = 0;
  // Measure and place each text/rect on wrapped lines
  for (let t = 0; t < text.length && i < limit; ++t) {
    const item = text[t];
    if (!item) {
      continue;
    }
    const isText = typeof item !== 'object';
    if (!isText) {
      // Get size from item (e.g. size of legend key color box)
      const { width, height } = item;
      addItem({ width, height, rect: item });
      continue;
    }
    // For each line of text between newlines
    const lines = item.split(matchNewline);
    for (let l = 0; l < lines.length && i < limit; ++l) {
      if (l) {
        // End previous line if not first line
        addNewLine();
      }
      const trimmed = lines[l].trim();
      // If line is blank or contains only whitespace, add it and do next line
      if (!trimmed) {
        addNewLine();
        addItem({ ...spaceSize, text: '', width: 0 });
        continue;
      }
      // Measure and add each word to wrapped lines
      const words = trimmed.split(matchWhitespace);
      for (let w = 0; w < words.length && i < limit; ++w) {
        const word = words[w];
        const wordSize = getCanvasTextSize(word, options);
        addItem({ ...wordSize, text: word }, w);
      }
    }
  }

  // Measure final dimensions of all lines combined
  for (let l = 0, lineLen = result.lines.length; l < lineLen; ++l) {
    const line = result.lines[l];
    resize(result, line.width, line.height, true);
  }

  return result;

  function addItem(item, delimiter = false) {
    if (!result.lines.length) {
      addNewLine();
    }
    let line = getLastLine();
    let addedWidth = (delimiter ? spaceSize.width : 0) + item.width;
    if (line.width && line.width + addedWidth > width) {
      addNewLine();
      line = getLastLine();
      addedWidth = item.width;
    }
    let lastItem = line.line[line.line.length - 1];
    if (!lastItem || item.rect || lastItem.rect) {
      line.line.push(item);
    } else {
      lastItem.text += ' ' + item.text;
      resize(lastItem, addedWidth, item.height);
    }
    resize(line, addedWidth, item.height);
    ++i;
  }

  function addNewLine() {
    if (gap && result.lines.length) {
      getLastLine().height += gap;
    }
    result.lines.push({ width: 0, height: 0, line: [] });
  }

  function getLastLine() {
    return result.lines[result.lines.length - 1];
  }

  function resize(size, width, height, vertical = false) {
    const op = [ sumOne, maxOne ];
    if (vertical) {
      op.reverse();
    }
    Object.assign(size, {
      width: op[0](width, size.width),
      height: op[1](height, size.height),
    });
  }
}
